import { Link } from "react-router-dom";
import { X, Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";

const fmt = (n) => `$${n.toFixed(2)}`;

export const CartDrawer = () => {
  const { isOpen, closeCart, items, subtotal, updateQty, removeItem } = useCart();

  return (
    <>
      <div
        onClick={closeCart}
        className={`fixed inset-0 z-[60] bg-shay-navy/60 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        data-testid="cart-backdrop"
      />
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 bottom-0 z-[70] w-full max-w-md bg-shay-cream text-shay-navy shadow-2xl transition-transform duration-500 ease-out flex flex-col ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-shay-gold/30">
          <div>
            <p className="eyebrow">Your Cart</p>
            <h3 className="font-display text-2xl mt-1">Sweet Selections</h3>
          </div>
          <button onClick={closeCart} data-testid="close-cart-button" className="h-10 w-10 grid place-items-center rounded-full hover:bg-shay-navy/5">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center" data-testid="cart-empty-state">
              <div className="h-20 w-20 rounded-full bg-shay-gold/15 grid place-items-center mb-5">
                <ShoppingBag size={28} className="text-shay-gold" />
              </div>
              <p className="font-display text-2xl">Nothing sweet yet.</p>
              <p className="mt-2 text-sm text-shay-chocolate/70 max-w-xs">
                Your cart is empty. Browse the menu and pick a treat — your future self will thank you.
              </p>
              <Link to="/menu" onClick={closeCart} className="btn-gold mt-7" data-testid="cart-browse-menu-link">
                Browse Menu
              </Link>
            </div>
          ) : (
            <ul className="space-y-6">
              {items.map((item) => (
                <li key={item.id} className="flex gap-4" data-testid={`cart-item-${item.id}`}>
                  <div className="h-20 w-20 shrink-0 overflow-hidden rounded-sm bg-shay-cream-deep">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-lg leading-tight truncate">{item.name}</p>
                    <p className="text-xs uppercase tracking-widest text-shay-brown mt-0.5">
                      {item.category}
                    </p>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="inline-flex items-center border border-shay-navy/20 rounded-full">
                        <button
                          onClick={() => updateQty(item.id, item.qty - 1)}
                          data-testid={`cart-decrease-${item.id}`}
                          className="h-8 w-8 grid place-items-center hover:text-shay-gold"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="w-7 text-center text-sm font-medium" data-testid={`cart-qty-${item.id}`}>{item.qty}</span>
                        <button
                          onClick={() => updateQty(item.id, item.qty + 1)}
                          data-testid={`cart-increase-${item.id}`}
                          className="h-8 w-8 grid place-items-center hover:text-shay-gold"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <p className="font-medium">{fmt(item.price * item.qty)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeItem(item.id)}
                    data-testid={`cart-remove-${item.id}`}
                    className="h-8 w-8 grid place-items-center text-shay-navy/40 hover:text-shay-gold"
                    aria-label="Remove item"
                  >
                    <Trash2 size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-shay-gold/30 px-6 pt-5 pb-6 space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-shay-chocolate/70">Subtotal</span>
              <span className="font-medium" data-testid="cart-subtotal">{fmt(subtotal)}</span>
            </div>
            <p className="text-[11px] text-shay-chocolate/60">
              Shipping, taxes and promo codes calculated at checkout.
            </p>
            <Link to="/checkout" onClick={closeCart} data-testid="cart-checkout-button" className="btn-gold w-full">
              Checkout · {fmt(subtotal)}
            </Link>
            <p className="text-[10px] uppercase tracking-widest text-shay-brown/70 text-center pt-1">
              Allergen Notice — Baked in a home kitchen with wheat, dairy, eggs, soy, peanuts & tree nuts.
            </p>
          </div>
        )}
      </aside>
    </>
  );
};

export default CartDrawer;
